import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthState, LoginRequest } from '../types/api';
import { api } from '../services/api';

interface AuthStore extends AuthState {
  // 액션들
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  verifyToken: () => Promise<boolean>;
  setToken: (token: string, username: string) => void;
  clearAuth: () => void;

  // 상태
  isLoading: boolean;
  error: string | null;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // 초기 상태
      isAuthenticated: false,
      token: null,
      username: null,
      isLoading: false,
      error: null,

      // 로그인
      login: async (credentials: LoginRequest) => {
        set({ isLoading: true, error: null });

        try {
          const response = await api.auth.login(credentials);
          const { token, username } = response;

          // 로컬스토리지에 토큰 저장
          localStorage.setItem('authToken', token);

          set({
            isAuthenticated: true,
            token,
            username,
            isLoading: false,
            error: null,
          });
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Login failed';
          set({
            isAuthenticated: false,
            token: null,
            username: null,
            isLoading: false,
            error: errorMessage,
          });
          throw error;
        }
      },

      // 로그아웃
      logout: async () => {
        try {
          await api.auth.logout();
        } catch (error) {
          console.error('로그아웃 API 호출 실패:', error);
        } finally {
          // 항상 로컬 상태는 초기화
          localStorage.removeItem('authToken');
          set({
            isAuthenticated: false,
            token: null,
            username: null,
            error: null,
          });
        }
      },

      // 토큰 검증
      verifyToken: async () => {
        const token = localStorage.getItem('authToken');
        if (!token) {
          set({ isAuthenticated: false, token: null, username: null });
          return false;
        }

        try {
          const response = await api.auth.verifyToken();
          if (response.valid) {
            set({
              isAuthenticated: true,
              token,
              username: response.username,
              error: null,
            });
            return true;
          } else {
            throw new Error('토큰이 유효하지 않습니다');
          }
        } catch (error) {
          localStorage.removeItem('authToken');
          set({
            isAuthenticated: false,
            token: null,
            username: null,
            error: '세션이 만료되었습니다',
          });
          return false;
        }
      },

      // 토큰 직접 설정
      setToken: (token: string, username: string) => {
        localStorage.setItem('authToken', token);
        set({
          isAuthenticated: true,
          token,
          username,
          error: null,
        });
      },

      // 인증 정보 초기화
      clearAuth: () => {
        localStorage.removeItem('authToken');
        set({
          isAuthenticated: false,
          token: null,
          username: null,
          error: null,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        token: state.token,
        username: state.username,
      }),
    }
  )
);